import { useState, useEffect, useCallback } from 'react';
import { contactService, analyticsService, feedbackService, migraineLogService } from '../firebase/database';

export const useFirebase = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleError = (err) => {
    console.error('Firebase error:', err);
    setError(err.message);
    setLoading(false);
  };

  const clearError = () => setError(null);

  return {
    loading,
    error,
    clearError,
    contactService: {
      submitContact: async (formData) => {
        setLoading(true);
        setError(null);
        try {
          const result = await contactService.submitContact(formData);
          setLoading(false);
          return result;
        } catch (err) {
          handleError(err);
          throw err;
        }
      }
    },
    analyticsService: {
      trackPageView: async (pageName, additionalData) => {
        try {
          return await analyticsService.trackPageView(pageName, additionalData);
        } catch (err) {
          console.warn('Analytics tracking failed:', err);
        }
      },
      trackInteraction: async (action, element, additionalData) => {
        try {
          return await analyticsService.trackInteraction(action, element, additionalData);
        } catch (err) {
          console.warn('Analytics tracking failed:', err);
        }
      },
      trackFormSubmission: async (formName, success, additionalData) => {
        try {
          return await analyticsService.trackFormSubmission(formName, success, additionalData);
        } catch (err) {
          console.warn('Analytics tracking failed:', err);
        }
      },
      trackConversion: async (conversionType, value, additionalData) => {
        try {
          return await analyticsService.trackConversion(conversionType, value, additionalData);
        } catch (err) {
          console.warn('Analytics tracking failed:', err);
        }
      },
      getPageViews: async (pageName, startDate, endDate) => {
        setLoading(true);
        setError(null);
        try {
          const result = await analyticsService.getPageViews(pageName, startDate, endDate);
          setLoading(false);
          return result;
        } catch (err) {
          handleError(err);
          throw err;
        }
      },
      getPageViewStats: async (pageName, days) => {
        setLoading(true);
        setError(null);
        try {
          const result = await analyticsService.getPageViewStats(pageName, days);
          setLoading(false);
          return result;
        } catch (err) {
          handleError(err);
          throw err;
        }
      },
      getSessionData: async (sessionId) => {
        setLoading(true);
        setError(null);
        try {
          const result = await analyticsService.getSessionData(sessionId);
          setLoading(false);
          return result;
        } catch (err) {
          handleError(err);
          throw err;
        }
      },
      getRealTimeStats: async () => {
        setLoading(true);
        setError(null);
        try {
          const result = await analyticsService.getRealTimeStats();
          setLoading(false);
          return result;
        } catch (err) {
          handleError(err);
          throw err;
        }
      },
      getSessionId: () => analyticsService.getSessionId(),
      getDeviceInfo: () => analyticsService.getDeviceInfo()
    },
    feedbackService: {
      submitFeedback: async (feedbackData) => {
        setLoading(true);
        setError(null);
        try {
          const result = await feedbackService.submitFeedback(feedbackData);
          setLoading(false);
          return result;
        } catch (err) {
          handleError(err);
          throw err;
        }
      }
    },
    migraineLogService: {
      logEntry: async (entryData) => {
        setLoading(true);
        setError(null);
        try {
          const result = await migraineLogService.logEntry(entryData);
          setLoading(false);
          return result;
        } catch (err) {
          handleError(err);
          throw err;
        }
      },
      getRecentLogs: async (userId, limitCount) => {
        setLoading(true);
        setError(null);
        try {
          const result = await migraineLogService.getRecentLogs(userId, limitCount);
          setLoading(false);
          return result;
        } catch (err) {
          handleError(err);
          throw err;
        }
      }
    }
  };
};

// Enhanced hook for tracking page views with section tracking
export const usePageTracking = (pageName, section = null) => {
  const { analyticsService } = useFirebase();
  const [sessionId] = useState(() => analyticsService.getSessionId());

  useEffect(() => {
    if (pageName) {
      const additionalData = {};
      if (section) additionalData.section = section;
      
      analyticsService.trackPageView(pageName, additionalData);
    }
  }, [pageName, section, analyticsService]);

  return { sessionId };
};

// Hook for tracking section visibility
export const useSectionTracking = (sectionName, threshold = 0.5) => {
  const { analyticsService } = useFirebase();
  const [hasTracked, setHasTracked] = useState(false);

  const ref = useCallback((node) => {
    if (node && !hasTracked) {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting && entry.intersectionRatio >= threshold) {
            analyticsService.trackInteraction('view', sectionName, {
              visibilityRatio: entry.intersectionRatio,
              boundingRect: entry.boundingClientRect
            });
            setHasTracked(true);
            observer.disconnect();
          }
        },
        { threshold }
      );
      
      observer.observe(node);
      
      return () => observer.disconnect();
    }
  }, [sectionName, threshold, hasTracked, analyticsService]);

  return ref;
};

// Hook for tracking interactions
export const useInteractionTracking = () => {
  const { analyticsService } = useFirebase();

  const trackClick = (element, additionalData = {}) => {
    analyticsService.trackInteraction('click', element, additionalData);
  };

  const trackHover = (element, additionalData = {}) => {
    analyticsService.trackInteraction('hover', element, additionalData);
  };

  const trackScroll = (element, additionalData = {}) => {
    analyticsService.trackInteraction('scroll', element, additionalData);
  };

  return {
    trackClick,
    trackHover,
    trackScroll
  };
};
